import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useLoaderData } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteSite } from '~features/sites/api'
import { SitesRoot } from '~features/sites/types'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { fetchAllDivision, fetchDropDownIntegratorList, fetchSiteList } from '~in-app/shareable/index.api'
import { Division, Integrator } from '~in-app/shareable/index.types'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/sites/list')({
  component: SitesPage,
  loader: async () => {
    let integratorIds: Option[] = []
    let divisionIds: Option[] = []
    const privilegeValue = auth$.privileges.get()
    const defaultSiteListing = privilegeValue.has('0.3.1.0')
      ? await fetchSiteList({
        query: {
          queries: new URLSearchParams(window.location.search).get('queries') || undefined,
          divisionIds: new URLSearchParams(window.location.search).get('divisionIds') || undefined,
          integratorIds: new URLSearchParams(window.location.search).get('integratorIds') || undefined,
          sort: (defaultState$.sortList.get() || []).map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`).join(','),
          page: (() => {
            const match = new URLSearchParams(window.location.search).get('page')
            return match ? parseInt(match, 10) - 1 : 0
          })(),
          size: (() => {
            const match = new URLSearchParams(window.location.search).get('pageSize')
            return match ? parseInt(match, 10) : 20
          })(),
        },
      })
      : {} as ListingResponse<SitesRoot>

    const integratorListResponse = privilegeValue.has('0.3.0.0')
      ? await fetchDropDownIntegratorList()
      : { list: [] } as { list: Integrator[] }
    integratorIds = integratorListResponse?.list?.map((item: any) => ({
      label: item.name,
      value: item.id,
    }))
    const divisionResponse = privilegeValue.has('0.4.0.0')
      ? await fetchAllDivision()
      : { divisions: [] } as { divisions: Division[] }
    const flattenDivisions = (divisions: any[], parentId: string | null = null): any[] => {
      return divisions.reduce((acc: any[], division: any) => {
        acc.push({
          value: division.id,
          label: division.name,
          parentId: parentId,
        })
        if (division.mychilds && division.mychilds.length > 0) {
          acc = acc.concat(flattenDivisions(division.mychilds, division.id))
        }
        return acc
      }, [])
    }
    divisionIds = flattenDivisions(divisionResponse.divisions)
    return { integratorIds, divisionIds, privilegeValue, defaultSiteListing }
  },
  staticData: {
    privileges: ['0.3.1.0'],
  },
})
type SitesList = SitesRoot & { action: string }
const columnHelper = createColumnHelper<SitesList>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  divisionIds: string[] | undefined
  integratorIds: string[] | undefined
  isChange: boolean
  isOpenDeleteSites: boolean
  deleteSitesId: string | undefined
  deleteSitesName: string | undefined
}>({
  sortList: [
    { id: 'name', desc: true },
    { id: 'active', desc: true },
  ],
  pagination: 0,
  pageSize: 20,
  queries: '',
  divisionIds: undefined,
  integratorIds: undefined,
  isChange: false,
  isOpenDeleteSites: false,
  deleteSitesId: undefined,
  deleteSitesName: undefined,
})

function SitesPage() {
  const { integratorIds, divisionIds, privilegeValue, defaultSiteListing } = useLoaderData({ from: '/console/sites/list' })
  const navigate = Route.useNavigate()
  const dropdownItems = (value: SitesList) => {
    return [
      {
        label: 'Edit',
        disabled: !privilegeValue.has('0.3.1.2'),
        onClick: () =>
          navigate({ to: `/console/sites/edit/${value.id}` }),
        icon: (
          <Icon
            height="16"
            icon="lucide:edit-2"
            width="16"
          />
        ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteItem(value),
        disabled: !privilegeValue?.has('0.3.1.3'),
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Site Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('systemId', {
      cell: info => info.getValue() ?? '-',
      header: () => <span>System ID</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('active', {
      cell: info => (info.getValue() ? 'Active' : 'Inactive'),
      header: () => <span>Status</span>,
      enableSorting: true,
      size: 110,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue() ?? '-',
      header: () => <span>Description</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('division.name', {
      cell: info => info.getValue(),
      header: () => <span>Division</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('integrator.name', {
      cell: info => info.getValue(),
      header: () => <span>System Integrator</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable(defaultState$)
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.divisionIds.set(new URLSearchParams(window.location.search).get('divisionIds')?.split(','))
    state$.integratorIds.set(new URLSearchParams(window.location.search).get('integratorIds')?.split(','))
    state$.queries.set(filteredQueries)
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
    ...(auth$.clientId.get() && privilegeValue?.has('0.4.0.0')
      ? [{
          label: 'Divisions',
          type: 'dropdown' as const,
          isMultiSelect: true,
          options: divisionIds,
          value: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
          result: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
        }]
      : []),
    ...(auth$.privileges.get()?.has('0.3.0.0')
      ? [{
          label: 'Integrator',
          type: 'dropdown' as const,
          isMultiSelect: true,
          options: integratorIds,
          value: integratorIds.filter(integrators => new URLSearchParams(window.location.search).get('integratorIds')?.split(',').includes(integrators.value)),
          result: integratorIds.filter(integrators => new URLSearchParams(window.location.search).get('integratorIds')?.split(',').includes(integrators.value)),
        }]
      : []),
  ]
  const tableState$ = useObservable<TableState$<SitesList>>(() => ({
    id: 'sites-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    pagination: state$.pagination.get(),
    pageSize: state$.pageSize.get(),
    totalPage: 0,
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}

      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue
      const divisionFilter = value.find((item: Option) => item.label === 'Division')
      const divisionValue = divisionFilter?.result || []
      if (divisionValue.length > 0) {
        state$.divisionIds.set(divisionValue.map((item: { value: any }) => item.value))
        searchParams.divisionIds = divisionValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.divisionIds.set(undefined)
        searchParams.divisionIds = undefined
      }
      const integratorFilter = value.find((item: Option) => item.label === 'Integrator')
      const integratorValue = integratorFilter?.result || []
      if (integratorValue.length > 0) {
        state$.integratorIds.set(integratorValue.map((item: { value: any }) => item.value))
        searchParams.integratorIds = integratorValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.integratorIds.set(undefined)
        searchParams.integratorIds = undefined
      }
      state$.isChange.set(!state$.isChange.get())
      // Navigate with updated params
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(divisionValue.length > 0)) delete updatedSearch.divisionIds
          if (!(integratorValue.length > 0)) delete updatedSearch.integratorIds

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['site-listing'],
    queryFn: async ({ signal }) => {
      const response = await fetchSiteList({
        // abortCtrl: signal,
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          integratorIds: state$?.integratorIds?.get() ? (state$?.integratorIds?.get() ?? []).join(',') : undefined,
          divisionIds: state$?.divisionIds?.get() ? (state$?.divisionIds?.get() ?? []).join(',') : undefined,
          sort: state$.sortList.get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newSiteList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      })) as SitesList[]
      tableState$.data.set(newSiteList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }

      return response
    },
    initialData: defaultSiteListing,
  })

  const useDeleteSite = () => {
    const mutation = useMutation({
      mutationFn: deleteSite,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['site-listing'] })
        toast.success('Site deleted successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteSite()

  const handleDeleteItem = (value: SitesRoot) => {
    state$.deleteSitesId.set(value.id)
    state$.deleteSitesName.set(value.name)
    state$.isOpenDeleteSites.set(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Sites Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteSites.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteSitesName.get()}' site?`}
                onClose={() => { state$.isOpenDeleteSites.set(false), state$.deleteSitesId.set(undefined), state$.deleteSitesName.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteRoleId = state$.deleteSitesId.get()
                    if (deleteRoleId) {
                      deleteMutation.mutate(deleteRoleId as string)
                      state$.deleteSitesId.set(undefined)
                      state$.deleteSitesName.set(undefined)
                      state$.isOpenDeleteSites.set(false)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue?.has('0.3.1.1')}
          onClick={() => navigate({ to: '/console/sites/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
